import React from 'react';
import { Message, Transition, Sticky, Icon } from 'semantic-ui-react';
import { NotificationRow, GridList } from 'dyl-components';
import ICONS from '../../constants/Icons';
import './index.scss';

import ReactDOM from 'react-dom';

const COLORS =
'red' | 'orange' | 'olive' |
'green' | 'teal' | 'blue' |
'violet' | 'purple' | 'pink' |
'san-marino' | 'supernova' | 'atomic-tangerine' |
'task' | 'sequence' | 'lead';

const StyledMessage = ({color=COLORS, children, positive, error, info, opaque}) => {
    if (positive) {
        color = 'success';
    } else if (error) {
        color = 'error';
    } else if (info) {
        color = 'info';
    } 
    const solidClassName = opaque ? 'opaque' : 'solid';
    return (
        <Message className={`Message Message--${solidClassName}-${color}`}>
            {children}
        </Message>
    )
}

export const Notification = ({ ref, visible, contextRef, children, status, opaque }) => (
    <div ref={ref} className={`Notification`}>
        <Transition visible={visible} animation='fade' duration='500' >
            <Sticky context={contextRef}>
                <strong>
                <StyledMessage color={status} opaque={opaque}>
                    <Icon name={ICONS[status]} /> {children}
                </StyledMessage>
                </strong>
            </Sticky>
        </Transition>
    </div>
);

export const Alert = ({ status, opaque, message, visible, autoDismiss }) => (
    <Transition visible={visible} duration={500} animation='fade' transitionOnMount unmountOnHide onComplete={autoDismiss}>
        <div className={`Notification`}>
        <Sticky>
            <strong>
            <StyledMessage color={status} opaque={opaque}>
                <Icon name={ICONS[status]} /> {message}
            </StyledMessage>
            </strong>
        </Sticky>
        </div>
    </Transition>
);

export const PhoneCallMessage = ({ phoneCalls, ref, visible, status, opaque, onOpen, onReject, onAnswer, answerDisabled }) => (
    <div ref={ref} className={`PhoneCallMessage`}>
        <Transition visible={visible} animation='fade' duration='500' >
            <GridList>
                {phoneCalls.map((phoneCall, index) => {
                    const leg = phoneCall?.legs?.find(l => !l.extension) || phoneCall?.legs?.find(l => l.channel_uuid === phoneCall.uuid);
                    const color = phoneCall.call_type === "newlead" ? "lead"
                        : phoneCall.call_type === "sequence" ? "sequence"
                        : phoneCall.call_type === "task" ? "task"
                        : status;
                    return (
                        <GridList.Row key={index} style={{padding: '0px 0px'}}>
                            <StyledMessage color={color} opaque={opaque}>
                                <GridList.Cell className="LeftBlock" onClick={() => onOpen(phoneCall.uuid)}>
                                        <Icon className='fa-solid fa-phone-volume' />
                                        {phoneCall.direction === "internal" && <Icon className='fa-solid fa-people-pants-simple' />}
                                        {(phoneCall.call_type === "newlead" || phoneCall.call_type === "callback") && <Icon className='fas fa-file-user' />}
                                        {phoneCall.call_type === "sequence" && <Icon className='fas fa-sitemap' />}
                                        {phoneCall.call_type === "task" && <Icon className='fa-solid fa-clipboard-check' />}
                                        <span>{leg?.name || leg?.phone} {phoneCall.direction === "internal" && `(Ext. ${leg?.extension})`}</span>
                                </GridList.Cell>
                                <GridList.Cell className="RightBlock">
                                    <Icon className='fa-solid fa-phone answer' onClick={() => onAnswer(phoneCall.uuid, phoneCall.channel_uuid)} disabled={answerDisabled}/>
                                    <Icon className='fa-solid fa-phone-hangup reject' onClick={() => onReject(phoneCall.uuid, phoneCall.channel_uuid)} />
                                </GridList.Cell>
                            </StyledMessage>
                        </GridList.Row>
                    )
                })}
            </GridList>
        </Transition>
    </div>
);

export const NotificationInfo = ({notifications, onOpen, onClear}) => {
    return(
        <div className={`NotificationInfo`}>
            <GridList>
                {notifications.map((notification, index) => {
                    return (
                        <Transition
                            duration={500}
                            animation='fade'
                            transitionOnMount
                            unmountOnHide
                        >
                            <NotificationRow
                                key={index}
                                notification={notification}
                                isStatus={false}
                                isTime={false}
                                isBorder
                                onOpen={onOpen}
                                onClear={onClear}
                            />
                        </Transition>
                    )
                })}
            </GridList>
        </div>
)};

class DismissibleAlert extends React.Component {
    state = {
        visible: this.props.visible,
        notifications: this.props.notifications || [],
        phoneCalls: this.props.phoneCalls || []
    }

    dismiss() {
        if (this.props.selfDismissing) {
            setTimeout(() => {
                this.setState({ visible: false });
            }, (this.props.timeout || 2000));
        }
    }

    clearNotification (id) {
        let notifications = this.state.notifications;
        const indexToRemove = notifications.findIndex(notification => notification.notification_id === id);
        notifications.splice(indexToRemove, 1);
        this.setState({ notifications: notifications});
    }

    render() {
        return (
            this.props.isNotifcationAlert ?
                <NotificationInfo
                    {...this.props}
                    notifications={this.state.notifications}
                    autoDismiss={this.props.onDismiss}
                    onClear={this.clearNotification.bind(this)}
                    onOpen={this.props.onOpen}
                /> 
                :
                this.props.isNotifcationPhoneCall ?
                    <PhoneCallMessage
                        {...this.props}
                        phoneCalls={this.state.phoneCalls}
                    />
                    :
                    <Alert 
                        {...this.props}
                        visible={this.state.visible}
                        autoDismiss={this.dismiss.bind(this)}
                    />
        )
    }
}

Notification.alert = (message, status, selfDismissing = true, props) => {
    props = Object.assign({ message, status, selfDismissing }, props);
    return next(props);
}

NotificationInfo.alert = (notifications, onOpen, selfDismissing = true, onDismiss, isNotifcationAlert = true, props) => {
    props = Object.assign({ notifications, onOpen, selfDismissing, onDismiss, isNotifcationAlert }, props);
    return nextNotification(props);
}

PhoneCallMessage.alert = (phoneCalls, onOpen, onAnswer, onReject, answerDisabled, name, onDismiss, isNotifcationPhoneCall = true, status = "success", props) => {
    props = Object.assign({ phoneCalls, onOpen, onAnswer, onReject, answerDisabled, name, onDismiss, isNotifcationPhoneCall, status }, props);
    return nextPhoneCall(props);
}

function nextPhoneCall(props) {
    return new Promise((resolve, reject) => {
        const div = document.createElement("PhoneCalls");
        div.setAttribute("id", "PhoneCalls");
    
        const notification_div = document.getElementById("PhoneCalls");
        if(notification_div !==  null){
            notification_div.remove();
        }

        document.body.appendChild(div);
        
        const component = React.createElement(DismissibleAlert, Object.assign({}, props, {
            promise: { resolve, reject },
            willUnmount: () => {
                ReactDOM.unmountComponentAtNode(div);
                document.body.removeChild(div);
            }
        }));
        ReactDOM.render(component, div);
    })
}

function nextNotification(props) {
        return new Promise((resolve, reject) => {
            const div = document.createElement("Notifications");
            div.setAttribute("id", "Notifications");
        
            const notification_div = document.getElementById("Notifications");
            if(notification_div !==  null){
                notification_div.remove();
            }

            document.body.appendChild(div);
            
            const component = React.createElement(DismissibleAlert, Object.assign({}, props, {
                promise: { resolve, reject },
                willUnmount: () => {
                    ReactDOM.unmountComponentAtNode(div);
                    document.body.removeChild(div);
                }
            }));
            ReactDOM.render(component, div);
        })
}

function next(props) {
    return new Promise((resolve, reject) => {
        const div = document.createElement('div');

        document.body.appendChild(div);
      
        props.visible = true;

        const component = React.createElement(DismissibleAlert, Object.assign({}, props, {
            promise: { resolve, reject },
            willUnmount: () => {
                ReactDOM.unmountComponentAtNode(div);
                document.body.removeChild(div);
            }
        }));

        ReactDOM.render(component, div);
    })
}
